/**
 * Archivo Adjunto Form Dialog
 * Form for uploading files with camera support for photos
 */

import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { MultiImageCapture } from "@/components/ui/multi-image-capture";
import { toast } from "sonner";
import {
  useCreateArchivoAdjunto,
  useCreateArchivosAdjuntosBatch,
} from "@/hooks/use-archivos-adjuntos";
import { TIPOS_ARCHIVO, type TipoArchivo } from "@/hooks/use-archivos-adjuntos";

const formSchema = z.object({
  tipo: z.enum(["FOTO", "PDF", "DOCUMENTO", "RESULTADO_EXTERNO", "RECETA", "OTRO"]),
  nombre_archivo: z.string().min(1, "El nombre es requerido"),
  descripcion: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

interface CapturedImage {
  file: File;
  preview: string;
}

interface ArchivoAdjuntoFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pacienteId: number;
  pacienteNombre: string;
}

export function ArchivoAdjuntoFormDialog({
  open,
  onOpenChange,
  pacienteId,
  pacienteNombre,
}: ArchivoAdjuntoFormDialogProps) {
  const { t } = useTranslation();

  const [files, setFiles] = useState<File[]>([]);
  const [capturedPhotos, setCapturedPhotos] = useState<CapturedImage[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const createMutation = useCreateArchivoAdjunto();
  const createBatchMutation = useCreateArchivosAdjuntosBatch();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      tipo: "DOCUMENTO",
      nombre_archivo: "",
      descripcion: "",
    },
  });

  const tipoSeleccionado = watch("tipo");
  const esFoto = tipoSeleccionado === "FOTO";

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    setFiles((prev) => [...prev, ...selectedFiles]);
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const onSubmit = async (data: FormData) => {
    const allFiles = esFoto ? capturedPhotos.map((p) => p.file) : files;

    if (allFiles.length === 0) {
      toast.error(t("archivosAdjuntos.validation.fileRequired"));
      return;
    }

    setIsSubmitting(true);

    try {
      if (allFiles.length === 1) {
        await createMutation.mutateAsync({
          paciente: pacienteId,
          tipo: data.tipo as TipoArchivo,
          nombre_archivo: data.nombre_archivo || allFiles[0].name,
          archivo: allFiles[0],
          descripcion: data.descripcion,
        });
      } else {
        await createBatchMutation.mutateAsync({
          pacienteId,
          tipo: data.tipo as TipoArchivo,
          files: allFiles,
          descripcion: data.descripcion,
        });
      }

      toast.success(t("archivosAdjuntos.toast.created"));

      handleClose();
    } catch {
      toast.error(t("api.unknownError"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFiles([]);
    setCapturedPhotos([]);
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("archivosAdjuntos.newArchivo")}</DialogTitle>
          <DialogDescription>
            {t("archivosAdjuntos.newArchivoDesc")} {pacienteNombre}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Tipo de archivo */}
          <div className="space-y-2">
            <Label>{t("archivosAdjuntos.fields.type")}</Label>
            <Select
              value={tipoSeleccionado}
              onValueChange={(value) => {
                setValue("tipo", value as TipoArchivo);
                // Clear files when changing type
                setFiles([]);
                setCapturedPhotos([]);
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIPOS_ARCHIVO.map((tipo) => (
                  <SelectItem key={tipo.value} value={tipo.value}>
                    {tipo.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Camera section for FOTO type */}
          {esFoto && (
            <MultiImageCapture value={capturedPhotos} onChange={setCapturedPhotos} maxImages={20} />
          )}

          {/* File upload for non-FOTO types */}
          {!esFoto && (
            <div className="space-y-2">
              <Label>{t("archivosAdjuntos.fields.file")}</Label>
              <div className="flex gap-2">
                <Input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={
                    tipoSeleccionado === "PDF"
                      ? ".pdf"
                      : tipoSeleccionado === "DOCUMENTO"
                        ? ".jpg,.jpeg,.png,.pdf,.tiff,.tif"
                        : ".jpg,.jpeg,.png,.pdf,.doc,.docx"
                  }
                  onChange={handleFileChange}
                />
              </div>
              {files.length > 0 && (
                <div className="space-y-1">
                  {files.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 bg-muted rounded"
                    >
                      <span className="text-sm truncate">{file.name}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => removeFile(index)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Nombre del archivo */}
          <div className="space-y-2">
            <Label>{t("archivosAdjuntos.fields.name")}</Label>
            <Input
              {...register("nombre_archivo")}
              placeholder={t("archivosAdjuntos.namePlaceholder")}
              className="placeholder:text-muted-foreground/50"
            />
            {errors.nombre_archivo && (
              <p className="text-sm text-destructive">{errors.nombre_archivo.message}</p>
            )}
          </div>

          {/* Descripción */}
          <div className="space-y-2">
            <Label>{t("archivosAdjuntos.fields.description")}</Label>
            <Textarea
              {...register("descripcion")}
              placeholder={t("archivosAdjuntos.descriptionPlaceholder")}
              className="placeholder:text-muted-foreground/50"
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? t("common.loading") : t("archivosAdjuntos.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
